import {
  deleteApp,
  initializeApp,
  getApps,
  getApp,
  type FirebaseApp,
} from "firebase/app";
import {
  browserSessionPersistence,
  getAuth,
  setPersistence,
  signInWithCustomToken,
  signOut,
  type Auth,
} from "firebase/auth";
import { getDatabase } from "firebase/database";
import { getFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";

export interface FirebaseWebConfig {
  apiKey: string;
  appId: string;
  authDomain: string;
  databaseURL: string;
  messagingSenderId: string;
  projectId: string;
  storageBucket: string;
}

export interface ImpersonationSession {
  actorEmail: string;
  actorUid: string;
  expiresAt: string;
  returnUrl: string;
  sessionId: string;
  targetDisplayName: string | null;
  targetEmail: string | null;
  targetUid: string;
}

const impersonationStorageKey = "reelmagic.impersonation.v1";
let activeImpersonationAppName: string | null = null;

function resolveAuthDomain(defaultAuthDomain: string | undefined) {
  if (!defaultAuthDomain) {
    return undefined;
  }

  if (typeof window === "undefined") {
    return defaultAuthDomain;
  }

  const { hostname, host } = window.location;
  const isLocalhost =
    hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";

  // Keep localhost development on the Firebase-hosted authDomain. Local dev
  // typically runs on http://localhost and cannot serve https auth helpers.
  if (isLocalhost) {
    return defaultAuthDomain;
  }

  // For deployed environments, use the app's own host so redirect helpers are
  // first-party (paired with /__/auth and /__/firebase rewrites in next.config).
  return host;
}

function readFirebaseWebConfig(): FirebaseWebConfig | null {
  const rawAuthDomain = process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN;
  const config = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
    appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
    authDomain: resolveAuthDomain(rawAuthDomain),
    databaseURL:
      process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL ??
      "https://reelmagic-v2-default-rtdb.firebaseio.com/",
    messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
    storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  };

  if (Object.values(config).some((value) => !value)) {
    return null;
  }

  return config as FirebaseWebConfig;
}

export function isFirebaseWebConfigReady() {
  return readFirebaseWebConfig() !== null;
}

export function getDefaultFirebaseClientApp(): FirebaseApp {
  const config = readFirebaseWebConfig();

  if (!config) {
    throw new Error("Firebase web config is incomplete.");
  }

  return getApps().some((app) => app.name === "[DEFAULT]")
    ? getApp()
    : initializeApp(config);
}

export function getFirebaseClientApp(): FirebaseApp {
  if (activeImpersonationAppName) {
    return getApp(activeImpersonationAppName);
  }
  return getDefaultFirebaseClientApp();
}

function getServicesForApp(app: FirebaseApp) {
  const config = readFirebaseWebConfig();
  if (!config) {
    throw new Error("Firebase web config is incomplete.");
  }
  const functionsRegion = process.env.NEXT_PUBLIC_FIREBASE_FUNCTIONS_REGION;
  return {
    app,
    auth: getAuth(app),
    database: getDatabase(app, config.databaseURL),
    firestore: getFirestore(app),
    functions: functionsRegion ? getFunctions(app, functionsRegion) : getFunctions(app),
  };
}

export function getDefaultFirebaseClientServices() {
  return getServicesForApp(getDefaultFirebaseClientApp());
}

export function getFirebaseClientServices() {
  return getServicesForApp(getFirebaseClientApp());
}

export function assertFirebaseMutationAllowed() {
  if (activeImpersonationAppName) {
    throw new Error(
      "Changes are unavailable while viewing the app as another user.",
    );
  }
}

function impersonationAppName(sessionId: string) {
  return `reelmagic-impersonation-${sessionId}`;
}

function initializeImpersonationApp(sessionId: string) {
  const config = readFirebaseWebConfig();
  if (!config) throw new Error("Firebase web config is incomplete.");
  const name = impersonationAppName(sessionId);
  return getApps().find((app) => app.name === name) ?? initializeApp(config, name);
}

function isImpersonationSession(value: unknown): value is ImpersonationSession {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ImpersonationSession>;
  return (
    typeof candidate.actorEmail === "string" &&
    typeof candidate.actorUid === "string" &&
    typeof candidate.expiresAt === "string" &&
    typeof candidate.returnUrl === "string" &&
    typeof candidate.sessionId === "string" &&
    typeof candidate.targetUid === "string" &&
    (candidate.targetDisplayName === null || typeof candidate.targetDisplayName === "string") &&
    (candidate.targetEmail === null || typeof candidate.targetEmail === "string")
  );
}

async function hasValidImpersonationClaims(auth: Auth, session: ImpersonationSession) {
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user || user.uid !== session.targetUid) return false;
  const token = await user.getIdTokenResult(true);
  return (
    token.claims.impersonating === true &&
    token.claims.impersonationMode === "read_only" &&
    token.claims.impersonationSessionId === session.sessionId &&
    token.claims.impersonationActorUid === session.actorUid &&
    typeof token.claims.impersonationExpiresAt === "number" &&
    token.claims.impersonationExpiresAt * 1000 > Date.now()
  );
}

function removeStoredImpersonationSession() {
  try {
    window.sessionStorage.removeItem(impersonationStorageKey);
  } catch {
    // Blocked browser storage must not prevent Firebase session cleanup.
  }
}

export async function restoreImpersonationFirebaseSession(): Promise<{
  auth: Auth;
  session: ImpersonationSession;
} | null> {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(impersonationStorageKey);
  } catch {
    // Impersonation storage is optional for normal authentication.
    return null;
  }
  if (!raw) return null;
  try {
    const session: unknown = JSON.parse(raw);
    if (!isImpersonationSession(session) || Date.parse(session.expiresAt) <= Date.now()) {
      removeStoredImpersonationSession();
      return null;
    }
    const app = initializeImpersonationApp(session.sessionId);
    const auth = getAuth(app);
    if (!(await hasValidImpersonationClaims(auth, session))) {
      removeStoredImpersonationSession();
      await signOut(auth).catch(() => undefined);
      await deleteApp(app);
      return null;
    }
    activeImpersonationAppName = app.name;
    return { auth, session };
  } catch {
    removeStoredImpersonationSession();
    return null;
  }
}

export async function startImpersonationFirebaseSession(
  session: ImpersonationSession,
  customToken: string,
) {
  const app = initializeImpersonationApp(session.sessionId);
  const auth = getAuth(app);
  try {
    await setPersistence(auth, browserSessionPersistence);
    await signInWithCustomToken(auth, customToken);
    if (!(await hasValidImpersonationClaims(auth, session))) {
      throw new Error("Firebase did not issue the required read-only impersonation claims.");
    }
    window.sessionStorage.setItem(impersonationStorageKey, JSON.stringify(session));
    activeImpersonationAppName = app.name;
    return auth;
  } catch (error) {
    await signOut(auth).catch(() => undefined);
    await deleteApp(app);
    throw error;
  }
}

export async function endImpersonationFirebaseSession() {
  const appName = activeImpersonationAppName;
  activeImpersonationAppName = null;
  removeStoredImpersonationSession();
  if (!appName) return;
  const app = getApps().find((candidate) => candidate.name === appName);
  if (!app) return;
  await signOut(getAuth(app)).catch(() => undefined);
  await deleteApp(app);
}
